import { useState } from 'react';
import { capitalCase } from 'change-case';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
// @mui
import { Tab, Box, Tabs, Card, Divider, Button, Stack, Typography, Paper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DoDisturbAltOutlinedIcon from '@mui/icons-material/DoDisturbAltOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import call from '../../../core/services/http';
// components

import useTabs from '../../../hooks/useTabs';
import Iconify from '../../../components/iconify/Iconify';
import { AssetDetailCard, AssetMaintenance, AssetTransferHistory, EditAssetModal, NewTransfer } from '.';
import { NewMaintenance } from '../../maintenance/components';
import { fCurrency } from '../../../utils/formatNumber';
import { AssetAudit } from '../../audit/components';
import { NewDisposal } from '../../disposals/components';
import { getAssetDetail } from '../store/assetActions';

const storedUser = JSON.parse(localStorage.getItem('profile'));
const branchRoles = ['MANAGER', 'ASST_MANAGER'];

const isManager = storedUser?.role && branchRoles.includes(storedUser.role);

const toastrSuccess = (msg) => {
  toast.success(msg);
};
const toastrError = (msg) => {
  toast.error(msg);
};
export default function AssetTabs() {
  const dispatch = useDispatch();
  const { depreciationData, asset } = useSelector((state) => state.assetReducer);
  const { id } = useParams();
  const { currentTab, onChangeTab } = useTabs('details');
  const [openRepair, setOpenRepair] = useState(false);
  const [openDispose, setOpenDispose] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);

  const ACCOUNT_TABS = [
    {
      value: 'details',
      icon: <InfoOutlinedIcon />,
      component: <AssetDetailCard recordId={id} />,
    },
    {
      value: 'repair history',
      icon: <Iconify icon={'ic:round-receipt'} width={20} height={20} />,
      component: <AssetMaintenance assetId={id} />,
    },
    {
      value: 'audit history',
      icon: <Iconify icon={'ic:round-receipt'} width={20} height={20} />,
      component: <AssetAudit assetId={id} />,
    },
    {
      value: 'transfer history',
      icon: <SendOutlinedIcon />,
      component: <AssetTransferHistory assetId={id} />,
    },
  ];

  const handleRepair = () => {
    setOpenRepair(!openRepair);
  };
  const handleDisposal = () => {
    setOpenDispose(!openDispose);
  };

  const handleEdit = () => {
    setOpenEdit(!openEdit);
  };

  const handleTransfer = () => {
    setOpenTransfer(false);
    dispatch(getAssetDetail(id));
  };

  const handleToggleStatus = async () => {
    try {
      if (!asset || !asset.id) {
        console.error('Asset not defined');
        return;
      }

      const isInactive = asset.status === 'retired';
      const url = `assets/${asset.id}/`;
      
      console.log('isInactive:', isInactive);

      // Send patch with new status
      const response = await call('put', url, { status: isInactive ? 'active' : 'inactive' });

      if (response?.data?.status) {
        toastrSuccess(`${isInactive ? 'Activated' : 'Deactivated'} successfully`);
        dispatch(getAssetDetail(asset.id));
      } else {
        toastrError(response?.data?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error(error);
      toastrError('Something went wrong');
    }
  };

  return (
    <>
      {openRepair && <NewMaintenance open={openRepair} handleClose={handleRepair} assetId={id} />}
      {openDispose && <NewDisposal open={openDispose} handleClose={handleDisposal} assetId={id} />}
      {openEdit && <EditAssetModal open={openEdit} handleClose={handleEdit} asset={asset} />}
      {openTransfer && asset && (
        <NewTransfer open={openTransfer} handleClose={handleTransfer} assetIds={[asset.id]} />
      )}
      <Paper sx={{ py: 3 }} elevation={1}>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
          <Stack width={1} textAlign="center">
            <Typography variant="subtitle">DEPRECIATION</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              KES {fCurrency(depreciationData?.accumulated_depreciation)}
            </Typography>
          </Stack>

          <Stack width={1} textAlign="center">
            <Typography variant="subtitle">NETBOOK VALUE</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              KES {fCurrency(depreciationData?.netbook_value)}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
      <Card sx={{ mt: 5 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" sx={{ float: 'right' }} spacing={1}>
            {isManager && (
              <>
                <Button variant="contained" size="small" startIcon={<BuildCircleOutlinedIcon />} onClick={handleRepair}>
                  New Repair
                </Button>

                <Button variant="contained" size="small" startIcon={<SellOutlinedIcon />} onClick={handleDisposal}>
                  Dispose
                </Button>
              </>
            )}
            {!isManager && (
              <>
                <Button variant="contained" size="small" startIcon={<EditOutlinedIcon />} onClick={handleEdit}>
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  startIcon={<SendOutlinedIcon />}
                  onClick={() => setOpenTransfer(true)}
                >
                  Transfer
                </Button>
                {asset && (
                  <Button
                    variant="contained"
                    size="small"
                    color={asset.status === 'retired' ? 'success' : 'warning'}
                    startIcon={
                      asset.status === 'retired' ? <CheckCircleOutlineOutlinedIcon /> : <DoDisturbAltOutlinedIcon />
                    }
                    onClick={handleToggleStatus}
                  >
                    {asset.status === 'retired' ? 'Activate' : 'Deactivate'}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Box>
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={onChangeTab}
        >
          {ACCOUNT_TABS.map((tab) => (
            <Tab
              // disableRipple
              key={tab.value}
              label={capitalCase(tab.value)}
              iconPosition="start"
              icon={tab.icon}
              value={tab.value}
            />
          ))}
        </Tabs>
        <Divider />

        <Box sx={{ mb: 5 }} />

        {ACCOUNT_TABS.map((tab) => {
          const isMatched = tab.value === currentTab;
          return isMatched && <Box key={tab.value}>{tab.component}</Box>;
        })}
      </Card>
    </>
  );
}
